import { useState, useEffect } from "react";
import "./FeedbackResponse.css";
import { Portal } from "./Portal";

interface SuccessMessageProps {
  rating: number;
  onClose: () => void;
}

export const SuccessMessage = ({ rating, onClose }: SuccessMessageProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    // Pequeño retraso para activar la animación de entrada
    const timer = setTimeout(() => setIsVisible(true), 50);
    return () => clearTimeout(timer);
  }, []);

  // Función para obtener el título del mensaje de éxito
  const getSuccessTitle = (rating: number): string => {
    if (rating >= 1 && rating <= 6) {
      return "Gracias por tu honestidad";
    } else if (rating >= 7 && rating <= 8) {
      return "Gracias por tu respuesta";
    } else if (rating >= 9 && rating <= 10) {
      return "Gracias por tu calificación";
    }
    return "Gracias por tu feedback";
  };

  // Función para obtener el mensaje de éxito
  const getSuccessMessage = (rating: number): string => {
    if (rating >= 1 && rating <= 6) {
      return "Tu retroalimentación será tenida en cuenta por nuestro equipo para identificar oportunidades de mejora en VirtualPro.";
    } else if (rating >= 7 && rating <= 8) {
      return "Nos alegra saber que VirtualPro ha sido útil y valoramos mucho tus sugerencias para seguir mejorando la experiencia.";
    } else if (rating >= 9 && rating <= 10) {
      return "Nos motiva saber que VirtualPro aporta valor a tu práctica académica.";
    }
    return "Tu comentario será revisado por nuestro equipo para identificar oportunidades de mejora en VirtualPro.";
  };

  // Obtener el ícono según la calificación
  const getSuccessIcon = (rating: number): string => {
    if (rating >= 1 && rating <= 6) {
      return "icon-fc-sad";
    } else if (rating >= 7 && rating <= 8) {
      return "icon-fc-neutral";
    } else if (rating >= 9 && rating <= 10) {
      return "icon-fc-happy";
    }
    return "icon-fc-star";
  };

  // Función para manejar el cierre con animación
  const handleClose = () => {
    setIsClosing(true);
    // Esperar a que termine la animación antes de llamar a onClose
    setTimeout(() => {
      onClose();
    }, 300);
  };

  return (
    <Portal>
      <div
        className={`feedback-overlay ${isVisible ? "visible" : ""} ${
          isClosing ? "closing" : ""
        }`}
        onClick={(e) => {
          if (e.target === e.currentTarget) handleClose();
        }}
      >
        <div
          className={`feedback-modal ${isVisible ? "visible" : ""} ${
            isClosing ? "closing" : ""
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Botón de cierre en esquina superior derecha */}
          <button
            className="feedback-close-button"
            aria-label="Cerrar modal"
            onClick={handleClose}
          >
            ×
          </button>

          {/* Contenido principal centrado verticalmente */}
          <div className="feedback-center-content">
            <div className="feedback-header">
              <div className="feedback-icon-wrapper">
                <i className={`icon ${getSuccessIcon(rating)}`}></i>
              </div>

              <div className="feedback-text-content">
                <h2 className="feedback-title">{getSuccessTitle(rating)}</h2>
                <p className="feedback-description">
                  {getSuccessMessage(rating)}
                </p>
              </div>
            </div>
          </div>

          {/* Botón rojo en la parte inferior derecha */}
          <div className="feedback-footer">
            <button
              type="button"
              className="feedback-red-button"
              onClick={handleClose}
            >
              Continuar
            </button>
          </div>
        </div>
      </div>
    </Portal>
  );
};